import GraphQLAPI, {
    GraphQLResult
} from "@aws-amplify/api-graphql";
import {
    CreateResourceAssessmentScheduleAssociationInput,
    CreateResourceAssessmentScheduleAssociationMutation,
    CreateResourceAssessmentScheduleAssociationMutationVariables,
    DeleteResourceAssessmentScheduleAssociationMutationVariables,
    ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQuery,
    ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQueryVariables,
    ListResourceAssessmentScheduleAssociationsByResourceQuery,
    ListResourceAssessmentScheduleAssociationsByResourceQueryVariables,
    PermissionResourceType,
    ResourceAssessmentScheduleAssociation
} from "../../../API";
import {
    createResourceAssessmentScheduleAssociation,
    deleteResourceAssessmentScheduleAssociation
} from "../../../graphql/mutations";
import {
    listResourceAssessmentScheduleAssociationsByAssessmentSchedule,
    listResourceAssessmentScheduleAssociationsByResource
} from "../../../graphql/queries";

import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { ResourceAssessmentScheduleAssociationDAO } from "./ResourceAssessmentScheduleAssociationDAO";

export class GraphQLResourceAssessmentScheduleAssociationDAO implements ResourceAssessmentScheduleAssociationDAO {
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.Create.Failure";
    private static readonly DELETE_ATTEMPT_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.Delete.Attempt";
    private static readonly DELETE_FAILURE_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.Delete.Failure";
    private static readonly LIST_BY_RESOURCE_ATTEMPT_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListByResource.Attempt";
    private static readonly LIST_BY_RESOURCE_FAILURE_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListByResource.Failure";
    private static readonly LIST_BY_SCHEDULE_ATTEMPT_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListBySchedule.Attempt";
    private static readonly LIST_BY_SCHEDULE_FAILURE_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListBySchedule.Failure";
    private static readonly LIST_BY_SCHEDULE_AND_RESOURCE_ATTEMPT_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListByScheduleAndResource.Attempt";
    private static readonly LIST_BY_SCHEDULE_AND_RESOURCE_FAILURE_METRIC_NAME = "GraphQLResourceAssessmentScheduleAssociationDAO.ListByScheduleAndResource.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async create(resourceAssessmentScheduleAssociation: CreateResourceAssessmentScheduleAssociationInput): Promise<ResourceAssessmentScheduleAssociation> {
        this.logger.info(
            `Creating ResourceAssessmentScheduleAssociation record`,
            { resourceAssessmentScheduleAssociation },
            [GraphQLResourceAssessmentScheduleAssociationDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateResourceAssessmentScheduleAssociationMutation> = await GraphQLAPI.graphql({
                query: createResourceAssessmentScheduleAssociation,
                variables: { input: resourceAssessmentScheduleAssociation } satisfies CreateResourceAssessmentScheduleAssociationMutationVariables,
                authToken
            }) as GraphQLResult<CreateResourceAssessmentScheduleAssociationMutation>;
            const createdResourceAssessmentScheduleAssociation = result.data?.createResourceAssessmentScheduleAssociation;
            if (!createdResourceAssessmentScheduleAssociation) {
                throw new Error(`Unable to create ResourceAssessmentScheduleAssociation record`);
            }
            return createdResourceAssessmentScheduleAssociation;
        } catch (error) {
            const errorMessage = `Unable to create ResourceAssessmentScheduleAssociation record`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLResourceAssessmentScheduleAssociationDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async delete(id: string, version: number): Promise<void> {
        this.logger.info(
            `Deleting ResourceAssessmentScheduleAssociation record with id=${id}`,
            { id },
            [GraphQLResourceAssessmentScheduleAssociationDAO.DELETE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            await GraphQLAPI.graphql({
                query: deleteResourceAssessmentScheduleAssociation,
                variables: {
                    input: {
                        id,
                        _version: version
                    }
                } satisfies DeleteResourceAssessmentScheduleAssociationMutationVariables,
                authToken
            });
        } catch (error) {
            const errorMessage = `Unable to delete ResourceAssessmentScheduleAssociation record with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLResourceAssessmentScheduleAssociationDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async listByResource(
        resourceId: string,
        resourceType: PermissionResourceType
    ): Promise<Array<ResourceAssessmentScheduleAssociation>> {
        this.logger.info(
            `Listing ResourceAssessmentScheduleAssociation records by resourceId=${resourceId} and resourceType=${resourceType}`,
            { resourceId, resourceType },
            [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_RESOURCE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListResourceAssessmentScheduleAssociationsByResourceQuery> = await GraphQLAPI.graphql({
                query: listResourceAssessmentScheduleAssociationsByResource,
                variables: {
                    resourceId,
                    resourceType: {
                        eq: resourceType
                    },
                    filter: {
                        _deleted: {
                            ne: true
                        }
                    }
                } satisfies ListResourceAssessmentScheduleAssociationsByResourceQueryVariables,
                authToken
            }) as GraphQLResult<ListResourceAssessmentScheduleAssociationsByResourceQuery>;
            const associations = (result.data?.listResourceAssessmentScheduleAssociationsByResource?.items ?? []) as Array<ResourceAssessmentScheduleAssociation>;
            return associations;
        } catch (error) {
            const errorMessage = `Unable to list ResourceAssessmentScheduleAssociation records by resourceId=${resourceId} and resourceType=${resourceType}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_RESOURCE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async listBySchedule(assessmentScheduleId: string): Promise<Array<ResourceAssessmentScheduleAssociation>> {
        this.logger.info(
            `Listing ResourceAssessmentScheduleAssociation records by assessmentScheduleId=${assessmentScheduleId}`,
            { assessmentScheduleId },
            [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_SCHEDULE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQuery> = await GraphQLAPI.graphql({
                query: listResourceAssessmentScheduleAssociationsByAssessmentSchedule,
                variables: {
                    assessmentScheduleId,
                    filter: {
                        _deleted: {
                            ne: true
                        }
                    }
                } satisfies ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQueryVariables,
                authToken
            }) as GraphQLResult<ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQuery>;
            const associations = (result.data?.listResourceAssessmentScheduleAssociationsByAssessmentSchedule?.items ?? []) as Array<ResourceAssessmentScheduleAssociation>;
            return associations;
        } catch (error) {
            const errorMessage = `Unable to list ResourceAssessmentScheduleAssociation records by assessmentScheduleId=${assessmentScheduleId}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_SCHEDULE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async listByScheduleAndResource(assessmentScheduleId: string, resourceId: string): Promise<Array<ResourceAssessmentScheduleAssociation>> {
        this.logger.info(
            `Listing ResourceAssessmentScheduleAssociation records by assessmentScheduleId=${assessmentScheduleId} and resourceId=${resourceId}`,
            { assessmentScheduleId, resourceId },
            [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_SCHEDULE_AND_RESOURCE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQuery> = await GraphQLAPI.graphql({
                query: listResourceAssessmentScheduleAssociationsByAssessmentSchedule,
                variables: {
                    assessmentScheduleId,
                    resourceId: {
                        eq: resourceId
                    },
                    filter: {
                        _deleted: {
                            ne: true
                        }
                    }
                } satisfies ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQueryVariables,
                authToken
            }) as GraphQLResult<ListResourceAssessmentScheduleAssociationsByAssessmentScheduleQuery>;
            const associations = (result.data?.listResourceAssessmentScheduleAssociationsByAssessmentSchedule?.items ?? []) as Array<ResourceAssessmentScheduleAssociation>;
            return associations;
        } catch (error) {
            const errorMessage = `Unable to list ResourceAssessmentScheduleAssociation records by assessmentScheduleId=${assessmentScheduleId} and resourceId=${resourceId}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLResourceAssessmentScheduleAssociationDAO.LIST_BY_SCHEDULE_AND_RESOURCE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }
}

export const graphQLResourceAssessmentScheduleAssociationDAO: ResourceAssessmentScheduleAssociationDAO = new GraphQLResourceAssessmentScheduleAssociationDAO(
    ClientLoggerFactory.getClientLogger("graphQLResourceAssessmentScheduleAssociationDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);